import axios from 'axios';
import {
  REACT_APP_ADMIN_API_BASE_URL,
  REACT_APP_API_BASE_URL,
  isDevelopment,
} from '../config/env';

function getAdminApiBaseUrl(): string {
  const trimmed = (s: string) => s.replace(/\/+$/, '');
  if (REACT_APP_ADMIN_API_BASE_URL) {
    return trimmed(REACT_APP_ADMIN_API_BASE_URL);
  }
  if (isDevelopment) {
    return 'http://localhost:8000/api/admin';
  }
  const main = trimmed(REACT_APP_API_BASE_URL);
  return main ? `${main}/admin` : '';
}

const API_BASE_URL = getAdminApiBaseUrl();
const ANALYTICS_BASE_URL = `${API_BASE_URL}/analytics`;

/**
 * Service to handle all analytics-related API calls for the Master Admin Dashboard.
 */
export const analyticsService = {
  /**
   * Triggers a global re-aggregation of all analytics and ML features.
   * @param daysBack - Optional number of days to backfill (e.g., 14, 30).
   */
  refreshAnalytics: async (daysBack?: number) => {
    const response = await axios.post(`${ANALYTICS_BASE_URL}/refresh`, null, {
      params: { days_back: daysBack }
    });
    return response.data;
  },

  /**
   * Fetches high-level business KPIs (Users, Conversions, GMV).
   */
  getBusinessSummary: async (days: number = 30) => {
    const response = await axios.get(`${ANALYTICS_BASE_URL}/summary`, {
      params: { days }
    });
    return response.data;
  },

  /**
   * Fetches technical performance vitals (Latency, Error Rates).
   */
  getTechnicalHealth: async (days: number = 7) => {
    const response = await axios.get(`${ANALYTICS_BASE_URL}/health`, {
      params: { days }
    });
    return response.data;
  },

  /**
   * Fetches operational intelligence for vendors (Reliability, Acceptance, Revenue).
   * Supports vertical-specific filtering.
   */
  getVendorIntelligence: async (limit: number = 15, category: string | null = null) => {
    const response = await axios.get(`${ANALYTICS_BASE_URL}/operations/vendors`, {
      params: { limit, category }
    });
    return response.data;
  },

  /**
   * Fetches service-wise usage and conversion metrics.
   */
  getServiceIntelligence: async (days: number = 7) => {
    const response = await axios.get(`${ANALYTICS_BASE_URL}/services`, {
      params: { days }
    });
    return response.data;
  },

  /**
   * Fetches a detailed chronological journey for a specific user.
   */
  getUserJourney: async (userId: string) => {
    const response = await axios.get(`${ANALYTICS_BASE_URL}/user-journey/${userId}`);
    return response.data;
  },

  /**
   * Fetches a production-grade detailed intelligence profile for an ambulance vendor.
   */
  getDetailedVendorIntelligence: async (vendorId: string) => {
    const response = await axios.get(`${API_BASE_URL}/vendor/ambulance/${vendorId}`);
    return response.data;
  }
};
